/**
 * admin-api.ts — thin client for /api/admin/voices/* endpoints.
 *
 * Adds the Firebase Auth Bearer header automatically.
 */
import { auth } from "@/lib/firebase";

export class AdminApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function withAuth(init?: RequestInit): Promise<RequestInit> {
  const user = auth.currentUser;
  if (!user) throw new AdminApiError(401, "Not signed in");
  const token = await user.getIdToken();
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${token}`);
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return { ...init, headers };
}

export async function adminGet<T>(path: string): Promise<T> {
  const init = await withAuth({ method: "GET" });
  const res = await fetch(path, init);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new AdminApiError(res.status, (body as { error?: string }).error ?? `${res.status}`);
  }
  return (await res.json()) as T;
}

export async function adminPost<T>(path: string, body?: unknown): Promise<T> {
  const init = await withAuth({
    method: "POST",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const res = await fetch(path, init);
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new AdminApiError(res.status, (payload as { error?: string }).error ?? `${res.status}`);
  }
  return (await res.json()) as T;
}
